import {
  Variant,
  Size,
  ComposableButtonConfig,
  ComposableButtonBaseProps,
} from "../types";

const gapCommonFontWeightLetterSpacing: Pick<
  ComposableButtonConfig,
  "fontWeight" & "letterSpacing"
> = {
  fontWeight: "regular",
  letterSpacing: 0,
};

export const gapCommonFontSize: (
  ctaSize: Size,
  viewport?: ComposableButtonBaseProps["viewport"]
) => string = (ctaSize, viewport) => {
  const xlViewportFontSizeMap = {
    xl: "40px",
    large: "30px",
    medium: "18px",
    small: "16px",
    xs: "14px",
  };
  const fontSizeMap = {
    xl: "24px",
    large: "20px",
    medium: "16px",
    small: "16px",
    xs: "12px",
  };
  return viewport === "x-large"
    ? xlViewportFontSizeMap[ctaSize]
    : fontSizeMap[ctaSize];
};

const gapComposableButtonConfig = (): ComposableButtonConfig => ({
  defaults: {
    size: Size.small,
    variant: Variant.solid,
  },
  small: {
    [Variant.border]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "47px",
        lineHeight: 26 / 24,
        padding: ["8px", "20px", "9px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "46px",
        lineHeight: 22 / 20,
        padding: ["10px", "20px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "44px",
        lineHeight: 16 / 12,
        padding: ["13px", "20px", "11px"],
      },
    },
    [Variant.chevron]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 26 / 24,
        minHeight: "44px",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 22 / 20,
        minHeight: "44px",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 16 / 12,
        minHeight: "44px",
      },
    },
    [Variant.flat]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 26 / 24,
        minHeight: "44px",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 21 / 16,
        minHeight: "44px",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 16 / 12,
        minHeight: "44px",
      },
    },
    [Variant.outline]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "47px",
        lineHeight: 26 / 24,
        padding: ["8px", "20px", "9px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "46px",
        lineHeight: 22 / 20,
        padding: ["10px", "20px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "44px",
        lineHeight: 16 / 12,
        padding: ["13px", "20px", "11px"],
      },
    },
    [Variant.solid]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "47px",
        lineHeight: 26 / 24,
        padding: ["8px", "20px", "9px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "46px",
        lineHeight: 22 / 20,
        padding: ["10px", "20px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "45px",
        lineHeight: 18 / 16,
        padding: ["12px", "20px", "11px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "44px",
        lineHeight: 16 / 12,
        padding: ["13px", "20px", "11px"],
      },
    },
    [Variant.underline]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 26 / 24,
        minHeight: "44px",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 22 / 20,
        minHeight: "44px",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "44px",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 16 / 12,
        minHeight: "44px",
      },
    },
  },
  "x-large": {
    [Variant.border]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "57px",
        lineHeight: 1,
        padding: ["6px", "20px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "49px",
        lineHeight: 1,
        padding: ["8px", "20px", "7px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "40px",
        lineHeight: 20 / 18,
        padding: ["8px", "20px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "39px",
        lineHeight: 18 / 16,
        padding: ["9px", "14px", "8px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "36px",
        lineHeight: 17 / 14,
        padding: ["7px", "14px", "6px"],
      },
    },
    [Variant.chevron]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 20 / 18,
        minHeight: "auto",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "auto",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 17 / 14,
        minHeight: "auto",
      },
    },
    [Variant.flat]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 20 / 18,
        minHeight: "auto",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "auto",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 17 / 14,
        minHeight: "auto",
      },
    },
    [Variant.outline]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "57px",
        lineHeight: 1,
        padding: ["6px", "20px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "49px",
        lineHeight: 1,
        padding: ["8px", "20px", "7px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "40px",
        lineHeight: 20 / 18,
        padding: ["8px", "20px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "39px",
        lineHeight: 18 / 16,
        padding: ["9px", "14px", "8px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "36px",
        lineHeight: 17 / 14,
        padding: ["7px", "14px", "6px"],
      },
    },
    [Variant.solid]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "57px",
        lineHeight: 1,
        padding: ["6px", "20px"],
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "49px",
        lineHeight: 1,
        padding: ["8px", "20px", "7px"],
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "40px",
        lineHeight: 20 / 18,
        padding: ["8px", "20px"],
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "39px",
        lineHeight: 18 / 16,
        padding: ["9px", "14px", "8px"],
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        minHeight: "36px",
        lineHeight: 17 / 14,
        padding: ["7px", "14px", "6px"],
      },
    },
    [Variant.underline]: {
      [Size.xl]: {
        fontSize: gapCommonFontSize(Size.xl, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.large]: {
        fontSize: gapCommonFontSize(Size.large, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 1,
        minHeight: "auto",
      },
      [Size.medium]: {
        fontSize: gapCommonFontSize(Size.medium, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 20 / 18,
        minHeight: "auto",
      },
      [Size.small]: {
        fontSize: gapCommonFontSize(Size.small, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 18 / 16,
        minHeight: "auto",
      },
      [Size.xs]: {
        fontSize: gapCommonFontSize(Size.xs, "x-large"),
        ...gapCommonFontWeightLetterSpacing,
        lineHeight: 17 / 14,
        minHeight: "auto",
      },
    },
  },
});

export default gapComposableButtonConfig;
